import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { TableConfig } from '../utils/tableUtils';
import { preprocessMathTags, extractMathExpressions } from '../utils/mathUtils';
import { InlineMath } from './MathRenderer';

interface TableRendererProps {
  config: TableConfig;
  isRTL?: boolean;
  onError?: (error: Error) => void;
}

const renderInlineMathSegments = (text: string, keyPrefix: string): React.ReactNode[] => {
  const processed = preprocessMathTags(text);
  const expressions = extractMathExpressions(processed);

  if (!expressions.length) {
    return [<React.Fragment key={`${keyPrefix}-text-0`}>{processed}</React.Fragment>];
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  expressions.forEach((expr, idx) => {
    if (expr.start > cursor) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-text-${idx}`}>
          {processed.slice(cursor, expr.start)}
        </React.Fragment>
      );
    }
    const sanitized = expr.expression.replace(/\\\\/g, '\\');
    nodes.push(
      <InlineMath key={`${keyPrefix}-math-${idx}`}>
        {sanitized}
      </InlineMath>
    );
    cursor = expr.end;
  });

  if (cursor < processed.length) {
    nodes.push(
      <React.Fragment key={`${keyPrefix}-text-tail`}>
        {processed.slice(cursor)}
      </React.Fragment>
    );
  }

  return nodes;
};

const TableRenderer: React.FC<TableRendererProps> = ({ config, isRTL = true, onError }) => {
  try {
    const { title, headers, rows, alignment } = config;
    
    // Validate that we have the required data
    if (!headers || !Array.isArray(headers) || headers.length === 0) {
      throw new Error('Table headers are required');
    }
    
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
      throw new Error('Table rows are required');
    }
    
    const defaultAlignment = isRTL ? 'right' : 'left';

    const getColumnAlignment = (colIndex: number): 'left' | 'center' | 'right' => {
      const value = alignment && alignment[colIndex] ? alignment[colIndex] : defaultAlignment;
      const normalized = (value || defaultAlignment || 'right').toLowerCase();
      if (normalized === 'left' || normalized === 'center' || normalized === 'right') {
        return normalized;
      }
      return defaultAlignment === 'left' ? 'left' : 'right';
    };

    const containsRTL = (value?: string): boolean => {
      if (!value) return false;
      return /[\u0590-\u05FF\u0600-\u06FF]/.test(value);
    };

    const getDirection = (content?: string): 'rtl' | 'ltr' => {
      if (!content || !content.trim()) {
        return isRTL ? 'rtl' : 'ltr';
      }
      return containsRTL(content) ? 'rtl' : 'ltr';
    };

    const getAlignmentClass = (align: string): string => {
      const normalized = (align || '').toLowerCase();
      switch (normalized) {
        case 'center':
          return 'text-center';
        case 'left':
          return 'text-left';
        case 'right':
          return 'text-right';
        default:
          return defaultAlignment === 'right' ? 'text-right' : 'text-left';
      }
    };
    
    return (
      <div className="my-4 md:my-6 not-prose">
        {title && (
          <h4 className={`font-semibold mb-2 text-primary-700 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {renderInlineMathSegments(title, 'table-title')}
          </h4>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full border border-secondary-300 rounded-lg shadow-sm bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
            <thead className="bg-secondary-50">
              <tr>
                {headers.map((header, index) => {
                  const columnAlignment = getColumnAlignment(index);
                  return (
                    <th
                      key={index}
                      className={`px-3 md:px-4 py-2 md:py-3 font-semibold text-secondary-800 bg-secondary-100 border-b border-secondary-300 text-sm md:text-base ${getAlignmentClass(columnAlignment)}`}
                      dir={getDirection(header)}
                      style={{ textAlign: columnAlignment }}
                    >
                      {renderInlineMathSegments(header, `header-${index}`)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-secondary-25 transition-colors">
                  {row.map((cell, cellIndex) => {
                    const columnAlignment = getColumnAlignment(cellIndex);
                    return (
                      <td
                        key={cellIndex}
                        className={`px-3 md:px-4 py-2 md:py-3 text-secondary-700 border-b border-secondary-200 text-sm md:text-base align-top ${getAlignmentClass(columnAlignment)}`}
                        dir={getDirection(cell)}
                        style={{ textAlign: columnAlignment }}
                      >
                        {/* Render cell content with ReactMarkdown to handle math */}
                        <ReactMarkdown
                          remarkPlugins={[remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                          components={{
                            p: ({ children }) => <span>{children}</span>,
                            // Ensure math renders properly in table cells
                            span: ({ className, children, ...props }) => {
                              if (className && className.includes('katex')) {
                                return (
                                  <span 
                                    className={`${className} math-inline`} 
                                    style={{ 
                                      direction: 'ltr',
                                      unicodeBidi: 'isolate',
                                      display: 'inline-block',
                                      textAlign: 'left',
                                      verticalAlign: 'baseline'
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </span>
                                );
                              }
                              return <span className={className} {...props}>{children}</span>;
                            }
                          }}
                        >
                          {cell || ''}
                        </ReactMarkdown>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error rendering table:', error);
    if (onError) {
      onError(error as Error);
    }
    
    return (
      <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Error rendering table</p>
      </div>
    );
  }
};

// Removed - now using ReactMarkdown with KaTeX plugins for proper math rendering

export default TableRenderer;
